import { MoneyFormatter } from '@sembo-private/money';
import '@vue/compiler-dom';
import { setDefaultOptions } from 'date-fns';
import { createPinia } from 'pinia';
import 'regenerator-runtime/runtime';
import { createApp } from 'vue';

import {
  i18n,
  loadLanguageAsync,
} from '@apps/main/modules/localization/localizationSetup';
import router from '@apps/main/modules/router/routerSetup';

import { getLocale } from '@libs/sembo-dates';
import {
  getExperiment,
  initializeStatsig,
  setExperimentValue,
} from '@libs/feature-toggle';

import App from './App.vue';
import { addCustomDirectives } from './directives';
import './styles/main.scss';
import { decodeHTML } from './modules/helpers/dom';
import { createLogger } from './modules/plugins/logger';
import { languageLocale } from './modules/localization/localeUtils';
import { trackPerformance } from './modules/helpers/trackPerformance';

if (decodeHTML(location.href) !== location.href) {
  location.href = decodeHTML(location.href) ?? location.href;
}

const app = createApp(App);

const pinia = createPinia();
app.use(pinia);

(async function (): Promise<void> {
  await MoneyFormatter.GetInstanceAsnyc();
  await initializeStatsig();

  const isNewPopularitySortOrderExperiment = getExperiment(
    'alternate_sort_order'
  ).get('use_new_sort_order', false);
  setExperimentValue(
    'alternate_sort_order',
    isNewPopularitySortOrderExperiment
  );

  const isHidedayxyExperiment = getExperiment(
    'remove_day_x_-_y_in_trip_overview'
  ).get('hide_day_x_y', false);
  setExperimentValue(
    'remove_day_x_-_y_in_trip_overview',
    isHidedayxyExperiment
  );

  const isShowDone = getExperiment('list_pages_copy_done').get(
    'show_done',
    false
  );
  setExperimentValue('list_pages_copy_done', isShowDone);

  const isHideNumberOfHotelsAndConductExperiment = getExperiment(
    'hide_number_of_hotels_and_conduct'
  ).get('hide_number_of_hotels', false);
  setExperimentValue(
    'hide_number_of_hotels_and_conduct',
    isHideNumberOfHotelsAndConductExperiment
  );

  const isHideSuggestedBorderFlightExperiment = getExperiment(
    'hide_border_on_suggested_flight'
  ).get('hide_suggested_border', false);
  setExperimentValue(
    'hide_border_on_suggested_flight',
    isHideSuggestedBorderFlightExperiment
  );

  const isHideFlightDateExperiment = getExperiment(
    'remove_dates_from_flight_list'
  ).get('remove_dates_from_flight_list', false);
  setExperimentValue(
    'remove_dates_from_flight_list',
    isHideFlightDateExperiment
  );

  const isLandBellowHeaderInMobileExperiment = getExperiment(
    'land_bellow_header_in_mobile'
  ).get('show_bellow_header', false);
  setExperimentValue(
    'land_bellow_header_in_mobile',
    isLandBellowHeaderInMobileExperiment
  );

  const isNewButtonExperiment = getExperiment('new_button').get(
    'use_new_button',
    false
  );
  setExperimentValue('new_button', isNewButtonExperiment);

  const isNewTripSummaryExperiment = getExperiment('new_trip_summary').get(
    'show_new_trip_summary',
    false
  );
  setExperimentValue('new_trip_summary', isNewTripSummaryExperiment);

  const isActivitySuggestionsExperiment = getExperiment(
    'activity_suggestions'
  ).get('show_activity_suggestions', false);
  setExperimentValue('activity_suggestions', isActivitySuggestionsExperiment);

  const isPriceGuaranteeAlternativeTitleExperiment = getExperiment(
    'price_guarantee_alterantive_title'
  ).get('show_price_guarantee_alternative_title', false);
  setExperimentValue(
    'price_guarantee_alterantive_title',
    isPriceGuaranteeAlternativeTitleExperiment
  );

  const isMobileOverviewFilterPillsExperiment = getExperiment(
    'compact_mobile_filter_pills'
  ).get('show_compact_mobile_filters', false);
  setExperimentValue(
    'compact_mobile_filter_pills',
    isMobileOverviewFilterPillsExperiment
  );
  const isHotelDetailsRoomCardsGridLayoutExperiment = getExperiment(
    'hotel_details_room_cards_grid_layout'
  ).get('hotel_details_room_cards_grid_layout', false);
  setExperimentValue(
    'hotel_details_room_cards_grid_layout',
    isHotelDetailsRoomCardsGridLayoutExperiment
  );

  const isNewSelectedRoomButtonExperiment = getExperiment(
    'new_rooms_button'
  ).get('should_show_new_button', false);
  setExperimentValue('new_rooms_button', isNewSelectedRoomButtonExperiment);
})();

const logger = createLogger();
app.use(logger);

app.use(router);
app.use(i18n);

app.mount('#app');

addCustomDirectives(app);

setDefaultOptions({ locale: getLocale(languageLocale), weekStartsOn: 0 });

loadLanguageAsync(languageLocale);

trackPerformance();
